import React from 'react';
import { Row, Col, FormGroup, Button, Glyphicon } from 'react-bootstrap';
import { Label, DownloadFileLink } from 'fw/components';
import { Form, TextBox, EmailBox, TextAreaBox, Switch } from 'fw/forms';
import { DocumentUpload, PhoneBox, SdvetNotice } from 'areas/shared/controls';
import * as API from 'modules/app/API';

// NVBDC-1404 — one "Is the following information correct?" block per veteran owner (plan item
// 14), plus the veteran-disability + benefit-verification-letter conditional logic reused as-is
// from the classic wizard's veteranOwnerInfo/VeteranOwnerList.js (plan item 15) — same field
// names (veteranServiceConnectedDisabled, benefitVerificationLetterStorageToken/FileName), same
// "only show the letter upload once disability is Yes" behavior.
class VeteranOwnerCard extends React.Component {
    constructor(props) {
        super(props);
        this.state = { correct: true };
    }

    onToggleCorrect = (value) => {
        this.setState({ correct: value });
    };

    onChange = (form) => {
        this.props.onChange(form);
    };

    onDisabilityChange = (value) => {
        const owner = { ...this.props.owner, veteranServiceConnectedDisabled: value };
        this.props.onChange(owner);
    };

    onBenefitLetterChange = (file) => {
        const owner = {
            ...this.props.owner,
            benefitVerificationLetterStorageToken: file.storageToken,
            benefitVerificationLetterFileName: file.fileName
        };
        this.props.onChange(owner);
    };

    // Phones is an array field, not a single named value, so it isn't a Form-managed control like
    // firstName/lastName/email above — same reason CompanySection.js drives its own Phones editor
    // by hand instead of a TextBox. Updates go straight through the same onChange choke point
    // (VeteranOwnersSection.onOwnerChange) the Form above uses, so both stay in sync.
    handleAddPhone = () => {
        const phones = [...(this.props.owner.phones || [])];
        phones.push({
            phoneTypeCode: 'MOBILE',
            number: '',
            extension: '',
            phoneId: -Math.abs(Date.now() | 0)
        });

        this.props.onChange({ ...this.props.owner, phones });
    };

    handleRemovePhone = (i) => {
        const phones = [...(this.props.owner.phones || [])];
        phones.splice(i, 1);

        this.props.onChange({ ...this.props.owner, phones });
    };

    handlePhoneChange = (v, i) => {
        const phones = [...(this.props.owner.phones || [])];
        phones[i] = v;

        this.props.onChange({ ...this.props.owner, phones });
    };

    renderPhoneEditor = () => {
        const phones = this.props.owner.phones || [];

        return (
            <FormGroup>
                <Label htmlFor="phoneNumber" required>Phone Number(s)</Label>
                {phones.map((p, i) => (
                    <div key={p.phoneId} className="form-inline">
                        <PhoneBox
                            value={p}
                            showExtension
                            onChange={(v) => this.handlePhoneChange(v, i)}
                            required={i === 0 ? 'required' : ''}
                        />
                        <Button
                            bsStyle="link"
                            title="Remove Phone"
                            onClick={() => this.handleRemovePhone(i)}>&times;</Button>
                    </div>
                ))}
                {phones.length < 5 &&
                    <Button bsStyle="default" bsSize="xsmall" onClick={this.handleAddPhone}>
                        Add Phone<Glyphicon glyph="plus" style={{ marginLeft: 10 }} />
                    </Button>
                }
            </FormGroup>
        );
    };

    // Plain text only — no tel: links (matches CompanySection.js's read-only phone display).
    renderReadOnlyPhones = () => {
        const phones = this.props.owner.phones || [];
        if (phones.length === 0) return null;

        return phones.map((p, i) => (
            <React.Fragment key={p.phoneId || i}>
                {p.phoneTypeCode}: {p.countryCode} {p.number}{p.extension ? ` #${p.extension}` : ''}<br />
            </React.Fragment>
        ));
    };

    // Mirrors the mockup's "Veteran, ARMY" line — same gating condition the classic wizard's
    // VeteranOwnerList.js:588 uses to decide whether Branch of Service applies to this owner.
    renderVeteranBranch = () => {
        const owner = this.props.owner;
        if (!(owner.isOwnerVeteran || owner.order === 0)) return null;

        const branch = (this.props.serviceBranches || []).find(b => b.value === owner.serviceBranchId);
        if (!branch) return null;

        return <React.Fragment>Veteran, {branch.label}<br /></React.Fragment>;
    };

    // Same SDVOB/VOB designation ConnectToVOBService.cs:73 computes (`SDVS = x.VeteranServiceConnectedDisabled
    // ? "SDVOB" : "VOB"`), applied per-owner here to match the mockup ("SDVOB with 59% Ownership"
    // next to each veteran's own percentage) rather than OneSheetRenewal.js's header badge, which
    // only reflects the majority owner.
    ownershipLine = () => {
        const owner = this.props.owner;
        const designation = owner.veteranServiceConnectedDisabled ? 'SDVOB' : 'VOB';
        return `${designation} with ${owner.businessHeldPercent}% Ownership`;
    };

    render() {
        const owner = this.props.owner;

        return (
            <div style={{ marginBottom: '15px' }} id={`veteranOwnerCard-${this.props.index}`}>
                <Row>
                    <Col md={9}>
                        <Label>Is the following information correct?</Label>
                    </Col>
                    <Col md={3}>
                        <Switch
                            name={`ownerCorrect_${owner.veteranOwnerId}`}
                            labelLeft="No"
                            labelRight="Yes"
                            bsStyle="success"
                            value={this.state.correct}
                            onChange={this.onToggleCorrect}
                        />
                    </Col>
                </Row>
                {this.state.correct ? (
                    <p>
                        {owner.firstName} {owner.lastName}, {owner.title}<br />
                        {this.renderVeteranBranch()}
                        {this.ownershipLine()}<br />
                        {this.renderReadOnlyPhones()}
                        {owner.email}
                    </p>
                ) : (
                    <Form onChange={this.onChange} values={owner} ref={(form) => { this._form = form; }}>
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label htmlFor="firstName" required>First Name</Label>
                                    <TextBox name="firstName" placeholder="First Name" maxLength={30} />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label htmlFor="lastName" required>Last Name</Label>
                                    <TextBox name="lastName" placeholder="Last Name" maxLength={30} />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label htmlFor="title" required>Title</Label>
                                    <TextBox name="title" placeholder="Title" maxLength={25} />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <Label htmlFor="email" required>Email</Label>
                                    <EmailBox name="email" placeholder="Email" maxLength={100} />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                {this.renderPhoneEditor()}
                            </Col>
                        </Row>
                    </Form>
                )}
                {owner.sdvet ? (
                    // NVBDC-1403: SDVET permanently exempts this owner from the disability
                    // toggle/letter upload -- backend force-syncs veteranServiceConnectedDisabled
                    // to true for this owner (see GetVeteranOwnerInfo/UpdateVeteranOwnerInfo), so
                    // ownershipLine() above already reads "SDVOB" correctly with no change needed
                    // here.
                    <SdvetNotice />
                ) : (
                    <React.Fragment>
                        <Row>
                            <Col md={9}>
                                <Label htmlFor="veteranServiceConnectedDisabled">Does the Veteran have a Service Connected Disability?</Label>
                            </Col>
                            <Col md={3}>
                                <Switch
                                    name={`disability_${owner.veteranOwnerId}`}
                                    labelLeft="No"
                                    labelRight="Yes"
                                    bsStyle="primary"
                                    value={owner.veteranServiceConnectedDisabled}
                                    onChange={this.onDisabilityChange}
                                />
                            </Col>
                        </Row>
                        {owner.veteranServiceConnectedDisabled && (
                            <FormGroup>
                                <Label>
                                    Benefit Verification Letter from EBenefits or VA - <font style={{ color: 'red' }}>Do not Upload CVE Certificate</font>
                                    &nbsp;<span className="required-indicator">*</span>
                                </Label>
                                <DocumentUpload
                                    storageToken={owner.benefitVerificationLetterStorageToken}
                                    fileName={owner.benefitVerificationLetterFileName}
                                    onChange={this.onBenefitLetterChange}
                                />
                            </FormGroup>
                        )}
                    </React.Fragment>
                )}
            </div>
        );
    }
}

// NVBDC-1404 / GAP-1404-03 — "Were there any changes in ownership?" disclosure. Not a persisted
// certification field: SaveOneSheet logs it as a reviewer-log Comment/ReviewDocument (ReviewTypeCode
// VI, CommentTypeCode OWC) instead. The switch always starts at No, regardless of any prior
// disclosure — `ownershipChange` (this.props) is the live, editable draft (always starts blank/No,
// see OneSheetRenewal.loadOneSheet), while `ownershipChangeHistory` is the read-only content
// recovered from the latest reviewer-log entry, shown whenever the switch is No. Toggling to Yes is
// always a fresh disclosure with blank fields — never an edit of a past entry — so Saving with Yes
// logs a brand new reviewer-log entry rather than amending history.
class OwnershipChangeBlock extends React.Component {
    onToggle = (changed) => {
        this.props.onChange({ ...this.props.ownershipChange, changed });
    };

    onDescriptionChange = (value) => {
        this.props.onChange({ ...this.props.ownershipChange, description: value });
    };

    onFileChange = (file) => {
        this.props.onChange({
            ...this.props.ownershipChange,
            storageToken: file.storageToken,
            fileName: file.fileName
        });
    };

    render() {
        const oc = this.props.ownershipChange || {};
        const history = this.props.ownershipChangeHistory || {};

        return (
            <div className="one-sheet-field-block" id="oneSheetOwnershipChangeBlock">
                <Row>
                    <Col md={9}>
                        <Label htmlFor="ownershipChanged">Were there any changes in ownership?</Label>
                    </Col>
                    <Col md={3}>
                        <Switch
                            name="ownershipChanged"
                            labelLeft="No"
                            labelRight="Yes"
                            bsStyle="success"
                            value={!!oc.changed}
                            onChange={this.onToggle}
                        />
                    </Col>
                </Row>
                {oc.changed ? (
                    <React.Fragment>
                        <FormGroup>
                            <Label htmlFor="ownershipChangeDescription" required>Describe the changes</Label>
                            <TextAreaBox
                                name="ownershipChangeDescription"
                                placeholder="Describe the changes..."
                                maxLength={2500}
                                value={oc.description}
                                onChange={this.onDescriptionChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label required>Supporting Document</Label>
                            <DocumentUpload
                                storageToken={oc.storageToken}
                                fileName={oc.fileName}
                                onChange={this.onFileChange}
                            />
                        </FormGroup>
                    </React.Fragment>
                ) : (history.description || history.fileName) && (
                    <React.Fragment>
                        {/* history.description is HTML-encoded server-side (SaveOwnershipChange) since it's
                            free text from an untrusted user rendered elsewhere via dangerouslySetInnerHTML
                            (the admin's Reviewer Log) — rendered the same way here so encoded entities
                            (e.g. &amp;) display correctly instead of showing the literal escaped text. */}
                        <p id="ownershipChangeReadOnlyDescription" className="one-sheet-field-value" dangerouslySetInnerHTML={{ __html: history.description }} />
                        {history.fileName && (
                            <DownloadFileLink
                                storageToken={history.storageToken}
                                fileName={history.fileName}
                                buttonName={history.fileName}
                                buttonStyle="link"
                                wrapFileNameLength={40}
                            />
                        )}
                    </React.Fragment>
                )}
            </div>
        );
    }
}

class VeteranOwnersSection extends React.Component {
    constructor(props) {
        super(props);
        this.state = { serviceBranches: [] };
    }

    // Same lookup call areas/shared/controls/serviceBranchCombo/ServiceBranchCombo.js uses — loaded
    // once here (read-only display only, no combo needed) so each VeteranOwnerCard can resolve its
    // own serviceBranchId to a name for the "Veteran, {Branch}" mockup line.
    componentDidMount() {
        API.Certification.get('/query/getServiceBranches', {}, {
            success: (r) => {
                this.setState({
                    serviceBranches: r.data.items.map(item => ({ label: item.serviceBranchName, value: item.serviceBranchId }))
                });
            }
        }, { block: false });
    }

    onOwnerChange = (index, owner) => {
        const veteranOwnerList = this.props.veteranOwnerList.slice();
        veteranOwnerList[index] = owner;
        this.props.onChange(veteranOwnerList);
    };

    render() {
        const owners = this.props.veteranOwnerList;

        return (
            <React.Fragment>
                {owners && owners.map((owner, index) => (
                    <VeteranOwnerCard
                        key={owner.veteranOwnerId}
                        index={index}
                        owner={owner}
                        serviceBranches={this.state.serviceBranches}
                        onChange={updated => this.onOwnerChange(index, updated)}
                    />
                ))}
                <OwnershipChangeBlock
                    ownershipChange={this.props.ownershipChange}
                    ownershipChangeHistory={this.props.ownershipChangeHistory}
                    onChange={this.props.onOwnershipChangeChange}
                />
            </React.Fragment>
        );
    }
}

export default VeteranOwnersSection;



// WEBPACK FOOTER //
// ./src/areas/certification/oneSheet/sections/VeteranOwnersSection.js